import type { CurrentUser } from "@/lib/auth";
import {
  userHasGroup,
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
  userIsMonteure,
} from "@/lib/auth";
import {
  userCanApproveZeiterfassung,
  userCanManageAllAbsences,
} from "@/lib/zeiterfassungPermissions";
import { HOME_TILE_IDS, type HomeTileId } from "@/pages/home/homeLayout";
import { resolveHomePersona, type HomePersona } from "@/pages/home/homeRole";

export interface HomeTileAudience {
  persona: HomePersona;
  canApprove: boolean;
  isMonteur: boolean;
  isManagement: boolean;
  isAbrechnung: boolean;
  isBuero: boolean;
  canPersonalSelf: boolean;
  canTeamAbsences: boolean;
  canSubcontractors: boolean;
  canBwMaterial: boolean;
  canBwInquiries: boolean;
  canBwOrders: boolean;
}

function canUsePersonalSelf(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (
    userIsMonteure(user) ||
    userIsBuero(user) ||
    userIsAbrechnung(user) ||
    userIsManagement(user) ||
    userHasGroup(user, "Nachunternehmer") ||
    userHasGroup(user, "App-Admins") ||
    userHasGroup(user, "App-Tester")
  );
}

/** Büro, Admins und Tester sehen die Bestellwesen-Übersicht, reine Monteure nur ihr Material. */
function canUseBestellwesenDesk(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userIsBuero(user) || userHasGroup(user, "App-Admins") || userHasGroup(user, "App-Tester");
}

export function homeTileAudience(user: CurrentUser | null): HomeTileAudience {
  const desk = canUseBestellwesenDesk(user);
  return {
    persona: resolveHomePersona(user),
    canApprove: userCanApproveZeiterfassung(user),
    isMonteur: userIsMonteure(user),
    isManagement: userIsManagement(user),
    isAbrechnung: userIsAbrechnung(user),
    isBuero: userIsBuero(user),
    canPersonalSelf: canUsePersonalSelf(user),
    canTeamAbsences: userCanManageAllAbsences(user),
    canSubcontractors:
      userIsBuero(user) || userIsManagement(user) || userHasGroup(user, "App-Admins"),
    canBwMaterial: desk,
    canBwInquiries: desk,
    canBwOrders: userIsBuero(user) || userHasGroup(user, "App-Admins"),
  };
}

function inCatalog(allow: ReadonlySet<HomeTileId>): HomeTileId[] {
  return HOME_TILE_IDS.filter((id) => allow.has(id));
}

/**
 * Kacheln, die diese Person auswählen darf.
 * Mehrere Rollen werden vereinigt; die Startseiten-Priorität begrenzt hier nicht.
 */
export function permittedHomeTileIds(audience: HomeTileAudience, hasUser: boolean): HomeTileId[] {
  if (!hasUser) return [];
  const allow = new Set<HomeTileId>(["kpis", "todos", "mentions"]);
  if (audience.canApprove) allow.add("absences");
  if (audience.isMonteur || audience.persona === "general") allow.add("my-day");
  if (audience.isMonteur) allow.add("material");
  if (audience.isManagement) {
    allow.add("completions");
    allow.add("lieferscheine");
    allow.add("angebote");
    allow.add("rueckfragen");
  }
  if (audience.isAbrechnung) {
    allow.add("billing-signed");
    allow.add("billing-lieferscheine");
    allow.add("billing-drafts");
    allow.add("billing-questions");
    allow.add("billing-missing-numbers");
  }
  if (audience.isBuero) allow.add("office-points");
  if (audience.isBuero || audience.persona === "general") allow.add("orders");
  if (audience.canPersonalSelf) allow.add("personal-overview").add("personal-absences");
  if (audience.canTeamAbsences) allow.add("team-absences");
  if (audience.canSubcontractors) allow.add("subcontractors");
  if (audience.canBwMaterial) allow.add("bw-material-open").add("bw-material-work");
  if (audience.canBwInquiries) allow.add("bw-inquiries");
  if (audience.canBwOrders) allow.add("bw-orders-late");
  return inCatalog(allow);
}

/**
 * Kacheln, die ohne persönliche Auswahl erscheinen.
 * Entspricht der bisherigen rollenabhängigen Startseite.
 */
export function defaultHomeTileIds(
  audience: HomeTileAudience,
  options: { hasUser: boolean; hasKpis: boolean; showMaterial: boolean },
): HomeTileId[] {
  if (!options.hasUser) return [];
  const allow = new Set<HomeTileId>(["todos", "mentions"]);
  if (options.hasKpis) allow.add("kpis");
  if (audience.canApprove) allow.add("absences");
  const showMyDay =
    audience.persona === "field" ||
    audience.persona === "general" ||
    (audience.persona === "office" && audience.isMonteur);
  if (showMyDay) allow.add("my-day");
  if (options.showMaterial) allow.add("material");
  if (audience.persona === "management") {
    allow.add("completions");
    allow.add("lieferscheine");
    allow.add("angebote");
    allow.add("rueckfragen");
  }
  if (audience.isAbrechnung) allow.add("billing-missing-numbers");
  if (audience.persona === "billing") {
    allow.add("billing-signed");
    allow.add("billing-lieferscheine");
    allow.add("billing-drafts");
    allow.add("billing-questions");
  }
  if (audience.persona === "office") allow.add("office-points");
  if (audience.persona === "office" || audience.persona === "general") allow.add("orders");
  return inCatalog(allow);
}
